import { isNumber } from 'lodash-es'
import type { Point } from 'chart.js'
import WbTextBox from '../textBox'
import type { FrameSize } from '../frame'
import WbFrame, { frameSizes } from '../frame'
import WbArticleImage from '../articleImage'
import WbArticleDetails from '../articleDetails'
import WbShape from '../shape'
import WbImage from '../image'
import WbLine from '../line'
import WbModelDetails from '../modelDetails'
import WbDesignOption from '../designOption'
import type { IWhiteboardTemplate, IWhiteboardTemplateFileMapping, IWhiteboardTemplateOption } from './IWhiteboardTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, whiteboardConstants } from '@/models/constants'
import utils from '@/services/utils'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'
import type CatalogPriceGroup from '@/models/catalogPriceGroup'
import colorwayMerchImage from '@/assets/templateLayoutImages/colorwayMerch.png'
import type LinkedCustomer from '@/models/linkedCustomer'
import type DuneAsset from '@/models/duneAsset'

class ColorwayMerch implements IWhiteboardTemplate {
  id = whiteboardConstants.frameTemplatesId.colorwayMerch
  name = 'Colorway Merch'
  layoutImage = colorwayMerchImage
  settings = {
    articleImage: { width: 500, height: 500, scale: whiteboardConstants.generateSlideImageScaleFactor.medium },
    articleDetails: { fontFamily: 'Neue Plak', fontSize: 12, fontWeight: 'normal', fontStyle: 'normal' as 'normal' | 'italic', fill: '#B8B8B8', textAlign: 'center', attributes: [], attributePlaceHolder: '[Blank]', showLabels: false },
    modelDetails: { fontFamily: 'Neue Plak', fontSize: 12, fontWeight: 'normal', fontStyle: 'normal' as 'normal' | 'italic', fill: '#B8B8B8', textAlign: 'left', attributes: ['ModelNumber', '_RetailPrice'], attributesSeparator: ' / ', attributePlaceHolder: '[Blank]', showLabels: false },
    textBox: { fontFamily: 'Neue Plak', fontWeight: 'normal', fontStyle: 'normal' as 'normal' | 'italic', fill: '#000000', textAlign: 'center' },
  }

  imgSize: number = 150
  defaultImageSize: number = 105
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  titleHeight: number = 0
  titleFontSize: number = 27
  titlePadding: number = 10
  frameWidth: number = frameSizes.wide.width
  frameHeight: number = frameSizes.wide.height
  logoWidth = 155
  logoHeight = 87
  logoScaling = 20
  uaNameLogoWidth = 300
  uaNameLogoHeight = 38
  uaNameLogoScaling = 50
  headerHeight = 60
  leftMarginForModel = 30
  frameVerticalHeaderWidth = 70
  startPositionX = 0
  startPositionY = 0
  rightMargin = 20
  groupHeaderProp = {
    fontSize: 42,
    fontWeight: 'normal',
    fontStyle: 'normal',
    height: 80,
    topMargin: 30,
    leftMargin: 70,
    rightMargin: 70,
    bottomMargin: 70,
  }

  subGroupheaderProp = {
    fontSize: 42,
    fontWeight: 'normal',
    fontStyle: 'normal',
    height: 80,
    leftMargin: 60,
    rightMargin: 60,
    bottomMargin: 60,
  }

  subSubGroupheaderProp = {
    fontSize: 42,
    fontWeight: 'normal',
    fontStyle: 'normal',
    height: 20,
  }

  headerProp = {
    fontSize: 40,
    fontWeight: 'bold',
    fontStyle: 'normal',
    paddingBetweenTitleLabels: 20,
  }

  modelHeaderProp = {
    width: 180,
    height: 70,
    minimumWidth: 150, // 180 - modelname + 35 new or c/o text
    verticalSpacing: 60,
  }

  periodProp = {
    verticalSpacing: 10,
  }

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, defaultImageTypeForTemplates: string): IWhiteboardTemplateOption[] {
    const attributes: IMyAttribute[] = []
    for (const key in myAttributes) {
      attributes.push(myAttributes[key])
    }

    // TODO: Implement configurable options
    const options: IWhiteboardTemplateOption[] = []

    options.push({
      name: 'mainGroup',
      label: 'generateFrameDialog.steps.options.mainGroup',
      type: 'list',
      default: myAttributes.CollectionGroup ? 'CollectionGroup' : '',
      required: true,
      clearable: true,
      filterable: true,
      multiple: false,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    options.push({
      name: 'subGroup',
      label: 'generateFrameDialog.steps.options.subGroup',
      type: 'list',
      default: myAttributes.Gender ? myAttributes.Collection ? ['Gender', 'Collection'] : ['Gender'] : myAttributes.Collection ? ['Collection'] : [],
      required: true,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 2,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    options.push({
      name: 'subSubGroup',
      label: 'generateFrameDialog.steps.options.subSubGroup',
      type: 'list',
      default: myAttributes.StyleFamily ? ['StyleFamily'] : [],
      required: true,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 2,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    const defaultOptionsForDetails: string[] = []
    if (myAttributes.ColorwayCode) {
      defaultOptionsForDetails.push(myAttributes.ColorwayCode.SystemName)
    }
    if (myAttributes.ColorId) {
      defaultOptionsForDetails.push(myAttributes.ColorId.SystemName)
    }
    if (myAttributes.LogoColor_obj) {
      defaultOptionsForDetails.push(myAttributes.LogoColor_obj.SystemName)
    }
    options.push({
      name: 'attributesToDisplayForCarryoverArticles',
      label: 'generateFrameDialog.steps.options.attributesToDisplayForCarryoverArticles',
      type: 'attributeChooser',
      default: defaultOptionsForDetails,
      required: true,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 3,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })
    options.push({
      name: 'attributesToDisplayForNewArticles',
      label: 'generateFrameDialog.steps.options.attributesToDisplayForNewArticles',
      type: 'attributeChooser',
      default: defaultOptionsForDetails,
      required: true,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 3,
      data: attributes,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: defaultImageTypeForTemplates,
      data: whiteboardConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
    })
    options.push({
      name: 'includeDesignOptions',
      label: 'generateFrameDialog.steps.options.includeDesignOptions',
      type: 'bool',
      default: false,
      required: false,
    })
    options.push({
      name: 'fitToPage',
      label: 'generateFrameDialog.steps.options.fitToPage',
      type: 'bool',
      default: false,
      required: false,
    })
    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // options.push({
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // })

    return options
  }

  getFileMapping(): IWhiteboardTemplateFileMapping[] {
    const mapping: IWhiteboardTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    })
    return mapping
  }

  // TODO: Need to work on loading custom fonts
  async generate(catalog: CatalogDetails, indexedLinkedCatalogDetails: Record<string, CatalogDetails>, articlesData: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, myUsername: string, myUserId: number, currentCustomer: LinkedCustomer | undefined, startPosition: IPoint, direction: 'horizontal' | 'vertical', excelData?: Record<string, Record<string, string[]>>, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, isExporting?: boolean, designOptionAssetMap?: Record<string, DuneAsset[]>): Promise<IWbObject[]> {
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, startPosition, retailPg, wholesalePg, outletPg, designOptionAssetMap)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, startPosition: IPoint, retailPg?: CatalogPriceGroup, wholesalePg?: CatalogPriceGroup, outletPg?: CatalogPriceGroup, designOptionAssetMap?: Record<string, DuneAsset[]>): Promise<IWbObject[]> {
    const objects: IWbObject[] = []
    // const frameSize = options.frameSize
    this.imgSize = (this.settings.articleImage.width * this.settings.articleImage.scale) / 100
    // this.changePropsAsPerFrameSize(frameSize)
    this.startPositionX = startPosition.x ? startPosition.x : 0
    this.startPositionY = startPosition.y ? startPosition.y : 0
    const priceGroups: Record<string, CatalogPriceGroup | undefined> = {
      wholesale: wholesalePg,
      retail: retailPg,
      outlet: outletPg,
    }
    const headerGroups = await this.generateGroups(catalog, articles, options, myAttributes, priceGroups, designOptionAssetMap)
    let groupHeaderStartX = this.startPositionX
    let groupHeaderStartY = this.startPositionY
    const frameList: IWbObject[] = []
    const subHeaderObjects: IWbObject[] = []
    const headerObjects: IWbObject[] = []
    groupHeaderStartY += this.groupHeaderProp.height + this.groupHeaderProp.topMargin
    for (const [, groupValue] of headerGroups) {
      groupHeaderStartX += this.groupHeaderProp.leftMargin
      let subGroupStartX = groupHeaderStartX
      let subGroupStartY = groupHeaderStartY
      let maxSubSubHeaderFramesLeft = subGroupStartX
      subGroupStartX += this.subGroupheaderProp.leftMargin
      for (const [, subHeaderValue] of groupValue.subGroupMap) {
        subGroupStartY += 30
        subGroupStartY += this.subGroupheaderProp.height
        const subSubGroupStartX = subGroupStartX
        let subSubGroupStartY = subGroupStartY

        for (const [subSubGroupKey, subSubHeaderValue] of subHeaderValue.subSubGroupMap) {
          // generate all the frames for each subheader
          // after each sub header x position will remain same only y position will change
          // const headerOpt = {
          //   left: subSubGroupStartX,
          //   top: subSubGroupStartY,
          //   showLabels: false,
          //   width: this.frameWidth - 20,
          //   attributes: [options.subSubGroup],
          //   attributePlaceHolder: '[Blank]',
          //   fontFamily: this.font,
          //   fontSize: this.subSubGroupheaderProp.fontSize,
          //   fontWeight: this.subSubGroupheaderProp.fontWeight,
          //   textAlign: 'left',
          //   lock: false,
          //   fill: '#000000',
          // }
          // objects.push(await WbArticleDetails.loadArticleDetails(subSubHeaderValue.article, myAttributes, headerOpt))
          subSubGroupStartY += this.subSubGroupheaderProp.height
          const subSubHeaderData = await this.generateFramesForSubSubHeader(catalog, myAttributes, subSubHeaderValue, subSubGroupKey, options, subSubGroupStartX, subSubGroupStartY, priceGroups)
          objects.push(...subSubHeaderData.objects)
          frameList.push(...subSubHeaderData.frameObjects)
          subSubGroupStartY += subSubHeaderData.totalHeightIncreased
          if (maxSubSubHeaderFramesLeft < subSubHeaderData.frameLeft) {
            maxSubSubHeaderFramesLeft = subSubHeaderData.frameLeft
          }
        }
        subHeaderObjects.push(...await this.createSubHeaderObjects(subGroupStartX - this.subGroupheaderProp.leftMargin, subGroupStartY - this.subGroupheaderProp.height, maxSubSubHeaderFramesLeft - subSubGroupStartX, subSubGroupStartY - subGroupStartY + this.subGroupheaderProp.height, options.subGroup, subHeaderValue.article, myAttributes, catalog, priceGroups))
        subGroupStartY = subSubGroupStartY + this.subGroupheaderProp.bottomMargin
      }
      subGroupStartX = maxSubSubHeaderFramesLeft + this.subGroupheaderProp.rightMargin
      headerObjects.push(...await this.createGroupHeaderObjects(groupHeaderStartX - this.groupHeaderProp.leftMargin, groupHeaderStartY - this.groupHeaderProp.height - this.groupHeaderProp.topMargin, subGroupStartX - groupHeaderStartX, subGroupStartY - groupHeaderStartY + this.groupHeaderProp.height, options.mainGroup, groupValue.article, myAttributes, catalog, priceGroups))
      groupHeaderStartX = subGroupStartX + this.groupHeaderProp.rightMargin + 10
      // groupHeaderStartY = subGroupStartY + this.groupHeaderProp.bottomMargin + this.groupHeaderProp.topMargin
    }
    objects.push(...frameList)
    objects.push(...subHeaderObjects)
    objects.push(...headerObjects)
    return objects
  }

  async generateGroups(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, priceGroups?: Record<string, CatalogPriceGroup | undefined>, designOptionAssetMap?: Record<string, DuneAsset[]>) {
    const groups = new Map()
    const groupAttribute = options.mainGroup
    const subGroupAttributes = options.subGroup
    const subSubGroupAttributes = options.subSubGroup
    let attributesForGrouping: string[] = [groupAttribute].concat(options.subGroup).concat(options.subSubGroup).concat(['MerchDepartment', 'StyleFamily', 'Period', 'ArticleLifeCycle', 'ColorwayCode'])
    attributesForGrouping = attributesForGrouping.reduce((acu, attribute) => {
      if (utils.isDefined(myAttributes[attribute])) {
        acu.push(attribute)
      }
      return acu
    }, [] as Array<string>)
    utils.sort(articles, attributesForGrouping)
    for (let i = 0; i < articles.length; i++) {
      const article = articles[i]
      const groupValue = (await utils.getAttributeTypeSpecificValue(myAttributes[groupAttribute], article, appConfig.DB, catalog, priceGroups)).toLowerCase()
      let subGroupValue = ''
      for (let j = 0; j < subGroupAttributes.length; j++) {
        subGroupValue += (await utils.getAttributeTypeSpecificValue(myAttributes[subGroupAttributes[j]], article, appConfig.DB, catalog, priceGroups)).toLowerCase()
      }
      let subSubGroupValue = ''
      for (let j = 0; j < subSubGroupAttributes.length; j++) {
        subSubGroupValue += (await utils.getAttributeTypeSpecificValue(myAttributes[subSubGroupAttributes[j]], article, appConfig.DB, catalog, priceGroups)).toLowerCase()
      }
      if (groups.has(groupValue)) {
        if (groups.get(groupValue).subGroupMap.has(subGroupValue)) {
          if (groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.has(subSubGroupValue)) {
            if (groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.has(article.ModelNumber)) {
              if (groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.get(article.ModelNumber).periodMap.has(article.Period)) {
                if (groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.get(article.ModelNumber).periodMap.get(article.Period).length < 30) {
                  groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.get(article.ModelNumber).periodMap.get(article.Period).push(article)
                }
              }
              else {
                groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.get(article.ModelNumber).periodMap.set(article.Period, [article])
              }
            }
            else {
              const periodMap = new Map()
              periodMap.set(article.Period, [article])
              if (options.includeDesignOptions && designOptionAssetMap && designOptionAssetMap[article.ModelNumber]) {
                periodMap.set('designOption', designOptionAssetMap[article.ModelNumber])
              }
              groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.get(subSubGroupValue).modelGroupMap.set(article.ModelNumber, { periodMap, article })
            }
          }
          else {
            const periodMap = new Map()
            periodMap.set(article.Period, [article])
            if (options.includeDesignOptions && designOptionAssetMap && designOptionAssetMap[article.ModelNumber]) {
              periodMap.set('designOption', designOptionAssetMap[article.ModelNumber])
            }
            const modelGroupMap = new Map()
            modelGroupMap.set(article.ModelNumber, { periodMap, article })
            groups.get(groupValue).subGroupMap.get(subGroupValue).subSubGroupMap.set(subSubGroupValue, { modelGroupMap, article })
          }
        }
        else {
          const periodMap = new Map()
          periodMap.set(article.Period, [article])
          if (options.includeDesignOptions && designOptionAssetMap && designOptionAssetMap[article.ModelNumber]) {
            periodMap.set('designOption', designOptionAssetMap[article.ModelNumber])
          }
          const modelGroupMap = new Map()
          modelGroupMap.set(article.ModelNumber, { periodMap, article })
          const subSubGroupMap = new Map()
          subSubGroupMap.set(subSubGroupValue, { modelGroupMap, article })
          groups.get(groupValue).subGroupMap.set(subGroupValue, { subSubGroupMap, article })
        }
      }
      else {
        const periodMap = new Map()
        periodMap.set(article.Period, [article])
        if (options.includeDesignOptions && designOptionAssetMap && designOptionAssetMap[article.ModelNumber]) {
          periodMap.set('designOption', designOptionAssetMap[article.ModelNumber])
        }
        const modelGroupMap = new Map()
        modelGroupMap.set(article.ModelNumber, { periodMap, article })
        const subSubGroupMap = new Map()
        subSubGroupMap.set(subSubGroupValue, { modelGroupMap, article })
        const subGroupMap = new Map()
        subGroupMap.set(subGroupValue, { subSubGroupMap, article })
        groups.set(groupValue, { subGroupMap, article })
      }
    }
    return groups
  }

  async createSubHeaderObjects(left: number, top: number, width: number, height: number, subGroupKeys: Array<string>, article: MyArticle, myAttributes: Record<string, IMyAttribute>, catalog: CatalogDetails, priceGroups: Record<string, CatalogPriceGroup | undefined>) {
    const objects: IWbObject[] = []
    let subGroupValue = ''
    for (let j = 0; j < subGroupKeys.length; j++) {
      if (j !== 0) {
        subGroupValue += ' - '
      }
      const value = await utils.getAttributeTypeSpecificValue(myAttributes[subGroupKeys[j]], article, appConfig.DB, catalog, priceGroups)
      subGroupValue += utils.isDefined(value) && value !== '' ? value : '[Blank]'
    }
    const rectangleWidth = width + this.subGroupheaderProp.rightMargin + this.subGroupheaderProp.leftMargin
    const rectangleScaleX = rectangleWidth / 50
    const rectangleScaleY = (height + this.subGroupheaderProp.bottomMargin) / 50

    objects.push(this.createTextBoxObject(subGroupValue, left, top, width + this.subGroupheaderProp.rightMargin + this.subGroupheaderProp.leftMargin, this.subGroupheaderProp.fontSize, this.subGroupheaderProp.fontWeight, 'center', '#ffffff', '#686868'))
    objects.push(this.createRectangleObject(left, top, 50, 50, rectangleScaleX, rectangleScaleY, '#E2E8F0'))
    return objects
  }

  async createGroupHeaderObjects(left: number, top: number, width: number, height: number, groupKey: string, article: MyArticle, myAttributes: Record<string, IMyAttribute>, catalog: CatalogDetails, priceGroups: Record<string, CatalogPriceGroup | undefined>) {
    const objects: IWbObject[] = []
    const value = await utils.getAttributeTypeSpecificValue(myAttributes[groupKey], article, appConfig.DB, catalog, priceGroups)
    const rectangleWidth = width + this.groupHeaderProp.rightMargin + this.groupHeaderProp.leftMargin
    const rectangleScaleX = rectangleWidth / 50
    const rectangleScaleY = (height + this.groupHeaderProp.bottomMargin + this.groupHeaderProp.topMargin) / 50

    objects.push(this.createTextBoxObject(utils.isDefined(value) && value !== '' ? value : '[Blank]', left, top, width + this.groupHeaderProp.rightMargin + this.groupHeaderProp.leftMargin, this.groupHeaderProp.fontSize, this.groupHeaderProp.fontWeight, 'center', '#ffffff', '#686868'))
    objects.push(this.createRectangleObject(left, top, 50, 50, rectangleScaleX, rectangleScaleY, '#ffffff'))
    return objects
  }

  async generateFramesForSubSubHeader(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, subSubHeaderData, subSubGroupKey: string, options: Record<string, any>, startPointX: number, startPointY: number, priceGroup?: Record<string, CatalogPriceGroup | undefined>) {
    let frameLeft = startPointX
    let frameTop = startPointY
    let currentFrame = 1
    const objects: IWbObject[] = []
    const frameObjects: IWbObject[] = []
    let frameCount = 0
    let currentFrameObjects: IWbObject[] = []
    let frameStartPointLeft = frameLeft + 10 + this.leftMarginForModel
    let currentTop = frameTop + 25
    let subSubGroupValue = ''
    for (let j = 0; j < options.subSubGroup.length; j++) {
      subSubGroupValue += await utils.getAttributeTypeSpecificValue(myAttributes[options.subSubGroup[j]], subSubHeaderData.article, appConfig.DB, catalog, priceGroup)
    }
    if (subSubGroupValue === '') {
      subSubGroupValue = '[Blank]'
    }

    currentFrameObjects.push(...await this.createFrameHeader(catalog, subSubHeaderData.article, myAttributes, currentTop, frameStartPointLeft, priceGroup))
    currentTop += this.headerHeight
    frameStartPointLeft += this.frameVerticalHeaderWidth
    const modelsPeriodWidthRequiredData: Record<string, Record<string, number>> = {}
    const currentRowMaxModelHeight = 300
    this.imgSize = 105
    for (const [modelNumber, modelGroupValue] of subSubHeaderData.modelGroupMap) {
      modelsPeriodWidthRequiredData[modelNumber] = this.findSpacingForAllPeriods(modelGroupValue.periodMap)
    //   modelsDataCalculation[modelNumber] = findtheModelHeight(modelGroupValue, spacing)
    }
    // check how many models data is there in subgroup and depnding if some conditions are satisfield we need to resize the imageSize
    const rowSpace = this.frameWidth - this.leftMarginForModel - 10 - this.rightMargin - this.frameVerticalHeaderWidth
    const slideSpace = 2 * (this.frameWidth - this.leftMarginForModel - 10 - this.rightMargin - this.frameVerticalHeaderWidth)
    let remainingSpaceInRowT = rowSpace
    const modelChunks: {
      rows: {
        modelNumbers: string[]
        remainingWidth: number
      }[]
    }[] = []

    let currentChunk: {
      rows: {
        modelNumbers: string[]
        remainingWidth: number
      }[]
    } = { rows: [] }
    let currentRow: string[] = []
    let availableSpace = slideSpace
    let currentAvailableSpace = slideSpace
    for (const [modelNumber, modelGroupValue] of subSubHeaderData.modelGroupMap) {
      const requiredWidth = this.minimumModelWidthRequired(
        modelGroupValue.periodMap,
        modelsPeriodWidthRequiredData[modelNumber],
      ) + this.modelHeaderProp.verticalSpacing
      if (remainingSpaceInRowT < this.modelHeaderProp.minimumWidth + this.modelHeaderProp.verticalSpacing) {
        currentAvailableSpace -= remainingSpaceInRowT
        remainingSpaceInRowT = 0
      }
      if (requiredWidth >= slideSpace) {
        // full slide allocate
        if (availableSpace !== slideSpace) {
          // already few models are there then simply move to next slide

          currentChunk.rows.push({
            modelNumbers: currentRow,
            remainingWidth: remainingSpaceInRowT,
          })

          modelChunks.push(currentChunk)
          currentChunk = { rows: [] }
        }
        // Store current row with its remaining width
        currentChunk.rows.push({
          modelNumbers: [modelNumber],
          remainingWidth: 0,
        })
        currentChunk.rows.push({
          modelNumbers: [modelNumber],
          remainingWidth: 0,
        })
        modelChunks.push(currentChunk)
        currentChunk = { rows: [] }
        // rowT = 1
        currentRow = []
        availableSpace = slideSpace
        remainingSpaceInRowT = rowSpace
      }
      else if (currentAvailableSpace < requiredWidth) {
        currentChunk.rows.push({
          modelNumbers: currentRow,
          remainingWidth: remainingSpaceInRowT,
        })
        modelChunks.push(currentChunk)
        currentChunk = { rows: [] }
        currentRow = [modelNumber]

        if (requiredWidth > rowSpace) {
          currentChunk.rows.push({
            modelNumbers: [modelNumber],
            remainingWidth: 0,
          })
          currentRow = [modelNumber]
          const remainingFor2ndRow = requiredWidth - rowSpace

          remainingSpaceInRowT = rowSpace - remainingFor2ndRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : remainingFor2ndRow
          currentAvailableSpace = rowSpace - remainingFor2ndRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : remainingFor2ndRow
        }
        else {
          currentRow = [modelNumber]

          remainingSpaceInRowT = rowSpace - requiredWidth
          currentAvailableSpace = availableSpace - requiredWidth
        }
      }
      else {
        // available space is enough for the model
        if (remainingSpaceInRowT >= requiredWidth) {
          // can accomodate in row data
          currentRow.push(modelNumber)
          remainingSpaceInRowT -= (requiredWidth)
          currentAvailableSpace -= (requiredWidth)
        }
        else if (remainingSpaceInRowT === rowSpace) {
          if (requiredWidth > rowSpace) {
            currentChunk.rows.push({
              modelNumbers: [modelNumber],
              remainingWidth: 0,
            })
            currentRow = []
            const remainingFor2ndRow = (requiredWidth - rowSpace)
            remainingSpaceInRowT = (rowSpace - (remainingFor2ndRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : remainingFor2ndRow))
            currentAvailableSpace -= rowSpace
            currentAvailableSpace -= (remainingFor2ndRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : remainingFor2ndRow)
          }
          else {
            remainingSpaceInRowT = (rowSpace - requiredWidth)
            currentAvailableSpace -= requiredWidth
          }
          currentRow.push(modelNumber)
        }
        else if (requiredWidth > rowSpace) {
          currentRow.push(modelNumber)
          currentChunk.rows.push({
            modelNumbers: currentRow,
            remainingWidth: 0,
          })
          const spaceRequiredInSecondRow = requiredWidth - remainingSpaceInRowT
          currentRow = []
          currentRow.push(modelNumber)
          currentAvailableSpace -= remainingSpaceInRowT
          remainingSpaceInRowT = (rowSpace - (spaceRequiredInSecondRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : spaceRequiredInSecondRow))
          currentAvailableSpace -= (spaceRequiredInSecondRow < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : spaceRequiredInSecondRow)
        }
        else {
          currentAvailableSpace -= remainingSpaceInRowT
          // Store current row with its remaining width
          currentChunk.rows.push({
            modelNumbers: currentRow,
            remainingWidth: 0,
          })
          currentRow = [modelNumber]
          remainingSpaceInRowT = (rowSpace - requiredWidth)
          currentAvailableSpace -= requiredWidth
        }
      }
    }

    // Handle remaining row + chunk
    if (currentRow.length > 0) {
      currentChunk.rows.push({
        modelNumbers: currentRow,
        remainingWidth: remainingSpaceInRowT,
      })
    }
    if (currentChunk.rows.length > 0) {
      modelChunks.push(currentChunk)
    }
    let index = 0
    for (const chunk of modelChunks) {
      // each chunk has models check if we can do resizing or not
      if (index !== 0) {
        objects.push(...currentFrameObjects)
        frameObjects.push(new WbFrame(options.frameSize, { children: currentFrameObjects.map(obj => obj.id || ''), name: `${subSubGroupValue + (frameCount === 0 ? '' : `(${frameCount})`)}`, left: frameLeft, top: frameTop, sortOrder: frameCount, lock: true }))
        currentFrameObjects = []
        currentFrame++
        frameCount++
        // even frame
        if (currentFrame % 2 === 0) {
          frameLeft += this.frameWidth + 10
        }
        else {
          frameLeft = startPointX
          frameTop += this.frameHeight + 10
        }
        frameStartPointLeft = frameLeft + 10 + this.leftMarginForModel
        currentTop = frameTop + 25
        currentFrameObjects.push(...await this.createFrameHeader(catalog, subSubHeaderData.article, myAttributes, currentTop, frameStartPointLeft, priceGroup))
        currentTop += this.headerHeight
        frameStartPointLeft += this.frameVerticalHeaderWidth
      }
      index++
      let resize = false
      this.imgSize = 105
      const rowCount = chunk.rows.length
      const totalModels = chunk.rows.reduce((sum, row) => sum + row.modelNumbers.length, 0)
      if (options.fitToPage && rowCount !== 2 && totalModels < 4) {
        // 🔍 Check if any model has more than 10 articles
        let hasHeavyModel = false
        let totalArticles = 0

        for (const row of chunk.rows) {
          for (const modelNumber of row.modelNumbers) {
            const modelData = subSubHeaderData.modelGroupMap.get(modelNumber)
            for (const [, articles] of modelData.periodMap) {
              totalArticles += articles.length
              if (articles.length > 8) {
                hasHeavyModel = true
                break
              }
            }

            if (hasHeavyModel) { break }
          }
          if (hasHeavyModel) { break }
        }
        if (!hasHeavyModel) {
          resize = true
          if (resize) {
            this.imgSize = this.calculateImageSizeForCurrentFrame(this.imgSize, totalArticles, chunk.rows[0].remainingWidth)
          }
        }
      }
      let currentRowTop = currentTop
      let lastModelNumberInFirstRow = ''
      if (chunk.rows.length > 1 && chunk.rows[0].modelNumbers[chunk.rows[0].modelNumbers.length - 1] === chunk.rows[1].modelNumbers[0]) {
        lastModelNumberInFirstRow = chunk.rows[0].modelNumbers[chunk.rows[0].modelNumbers.length - 1]
      }
      let currentRow = 1
      let isModelDistributed = false
      let secondPartPeriodData: Record<string, any> = {}
      let periodsDone: string[] = []
      for (const currentChunkRow1 of chunk.rows) {
        for (const modelNum of currentChunkRow1.modelNumbers) {
          let remainingSpaceInRow = this.frameWidth - (frameStartPointLeft - frameLeft) - this.rightMargin

          if (lastModelNumberInFirstRow === modelNum) {
            isModelDistributed = true
          }
          else {
            isModelDistributed = false
            periodsDone = []
          }
          let currentModelTop = currentRowTop
          const modelGroupValue = subSubHeaderData.modelGroupMap.get(modelNum)
          if (this.imgSize !== 105) {
            modelsPeriodWidthRequiredData[modelNum] = this.findSpacingForAllPeriodsAsPerNewImageSize(modelGroupValue.periodMap, this.imgSize)
          }
          const minimumWidthRequiredByCurrentModel = this.minimumModelWidthRequired(modelGroupValue.periodMap, modelsPeriodWidthRequiredData[modelNum])
          const modelHeaderInfo = {
            top: currentModelTop,
            left: frameStartPointLeft,
          }
          currentModelTop += this.modelHeaderProp.height
          currentModelTop += 10 // margin below modelheader
          let modelLeft = frameStartPointLeft
          // Sort keys but move 'designOption' to the end
          const periodKeys: string[] = Array.from(modelGroupValue.periodMap.keys())
          const sortedMapKeys: string[] = periodKeys
            .filter(key => key !== 'designOption')
            .sort() // optional: sort other keys alphabetically
          if (periodKeys.includes('designOption')) {
            sortedMapKeys.push('designOption')
          }
          let designDataObjects: any[] = []
          for (let j = 0; j < sortedMapKeys.length; j++) {
            const period: string = sortedMapKeys[j]
            const articles = modelGroupValue.periodMap.get(period)
            if ((currentRow === 2 && periodsDone.includes(period)) || (isModelDistributed && currentRow === 1 && remainingSpaceInRow < this.imgSize + this.periodProp.verticalSpacing)) {
              continue
            }
            if (currentRow === 1 && period === 'designOption' && isModelDistributed && remainingSpaceInRow < modelsPeriodWidthRequiredData[modelNum][period]) {
              continue
            }
            const periodObjectAndData = await this.addPeriodAndArticles(period, articles, options, modelsPeriodWidthRequiredData[modelNum][period], modelGroupValue.article, currentModelTop, modelLeft, isModelDistributed && remainingSpaceInRow < modelsPeriodWidthRequiredData[modelNum][period] && period !== 'designOption', remainingSpaceInRow, isModelDistributed && currentRow === 2, secondPartPeriodData)
            designDataObjects = periodObjectAndData.designOptionsObjects ? periodObjectAndData.designOptionsObjects : []
            currentFrameObjects.push(...periodObjectAndData.objects)
            secondPartPeriodData = periodObjectAndData.distributedModelData
            modelLeft += periodObjectAndData.widthUsed ? periodObjectAndData.widthUsed < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : periodObjectAndData.widthUsed : modelsPeriodWidthRequiredData[modelNum][period] + this.periodProp.verticalSpacing
            if (currentRow === 1 && isModelDistributed && remainingSpaceInRow < modelsPeriodWidthRequiredData[modelNum][period]) {
              break
            }
            else {
              if (currentRow === 1 && isModelDistributed) {
                periodsDone.push(period)
              }
            }
            if (currentRow === 2) {
              isModelDistributed = false
            }
            remainingSpaceInRow -= periodObjectAndData.widthUsed ? periodObjectAndData.widthUsed < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : periodObjectAndData.widthUsed : modelsPeriodWidthRequiredData[modelNum][period] + this.periodProp.verticalSpacing
          }
          currentFrameObjects.push(...await this.addModelHeader(modelGroupValue.article, options.includeDesignOptions, modelHeaderInfo.top, modelHeaderInfo.left, minimumWidthRequiredByCurrentModel, designDataObjects))

          frameStartPointLeft = (modelLeft - frameStartPointLeft) < this.modelHeaderProp.minimumWidth ? frameStartPointLeft + this.modelHeaderProp.minimumWidth + this.modelHeaderProp.verticalSpacing : modelLeft + this.modelHeaderProp.verticalSpacing
        }
        frameStartPointLeft = frameLeft + 10 + this.leftMarginForModel + this.frameVerticalHeaderWidth
        currentRowTop += currentRowMaxModelHeight
        currentRow++
      }
      this.imgSize = 105
    }

    objects.push(...currentFrameObjects)
    frameObjects.push(new WbFrame(options.frameSize, { children: currentFrameObjects.map(obj => obj.id || ''), name: `${subSubGroupValue + (frameCount === 0 ? '' : `(${frameCount})`)}`, left: frameLeft, top: frameTop, sortOrder: frameCount, lock: true }))
    frameCount++
    currentFrameObjects = []
    const totalHeightIncreased = frameCount === 1 || frameCount === 2 ? this.frameHeight + 10 : ((Math.ceil(frameCount / 2) * this.frameHeight) + (Math.ceil(frameCount / 2) * 10))
    return { objects, frameObjects, totalHeightIncreased, frameLeft: startPointX + (frameCount === 1 ? this.frameWidth + 10 : 2 * (this.frameWidth + 10)) }
  }

  calculateImageSizeForCurrentFrame(imageSize, totalArticles, remainingWidth) {
    const newImageSize = Math.floor(remainingWidth / totalArticles)
    return (imageSize + newImageSize) > 450 ? 450 : imageSize + newImageSize
  }

  minimumModelWidthRequired(periodMap, periodsWidth: Record<string, number>) {
    let spacing = (periodMap.size * this.periodProp.verticalSpacing) + ((periodMap.size - 1) * this.periodProp.verticalSpacing)
    spacing += Object.values(periodsWidth).reduce((acc, obj) => acc + obj, 0) as number
    return spacing < this.modelHeaderProp.minimumWidth ? this.modelHeaderProp.minimumWidth : spacing
  }

  async createFrameHeader(catalog: CatalogDetails, article: MyArticle, myAttributes: Record<string, IMyAttribute>, frameTop: number, frameLeft: number, priceGroups?: Record<string, CatalogPriceGroup | undefined>) {
    const objects: IWbObject[] = []
    objects.push(await this.createHeaderLogo(frameTop, frameLeft + 20))
    const season = catalog.Season.toLowerCase().includes('ss') ? 'SPRING/SUMMER' : 'FALL/WINTER'
    objects.push(this.createTextBoxObject(season, frameLeft + 20, frameTop + this.frameHeight / 2 - 220, 60, 12, 'bold', 'center', '#000000', '', 90))
    objects.push(await WbModelDetails.loadModelDetails(article, { left: frameLeft + 20, top: frameTop + this.frameHeight / 2 - 80, width: 60, fontSize: 12, fontWeight: 'bold', textAlign: 'center', fill: '#000000', angle: 90, showLabels: this.settings.modelDetails.showLabels, attributes: ['Collection'], fontFamily: this.settings.modelDetails.fontFamily, allowDesignOption: false }))
    objects.push(this.createTextBoxObject(catalog.Year.toString(), frameLeft + 20, frameTop + this.frameHeight / 2 + 30, 60, 12, 'bold', 'center', '#000000', '', 90))
    objects.push(await this.createHeaderNameLogo(frameTop + this.frameHeight - 180, frameLeft + 25))

    const collectionValue = await utils.getAttributeTypeSpecificValue(myAttributes.Collection, article, appConfig.DB, catalog, priceGroups)
    objects.push(this.createTextBoxObject(collectionValue || '[Blank]', frameLeft + 40, frameTop + 10, this.frameWidth - 150, 20, 'bold', 'left'))
    return objects
  }

  findSpacingForAllPeriods(modelGroupValue) {
    let periodArticlesCountMap = {}
    const spacingForEachPeriod = {}
    for (const [period, articles] of modelGroupValue) {
      periodArticlesCountMap[period] = articles.length
    }
    periodArticlesCountMap = Object.entries(periodArticlesCountMap)
      .sort(([,a], [,b]) => a as number - (isNumber(b) ? b : 0))
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})

    let availableSpaceForEachPeriod = (2 * (this.frameWidth - this.leftMarginForModel - this.frameVerticalHeaderWidth - 10 - this.rightMargin - 30 - (modelGroupValue.size * 10) - ((modelGroupValue.size - 1) * 10))) / modelGroupValue.size
    const periods = Object.keys(periodArticlesCountMap)
    for (let i = 0; i < periods.length; i++) {
      if (Math.floor(availableSpaceForEachPeriod / this.defaultImageSize) <= periodArticlesCountMap[periods[i]]) {
        spacingForEachPeriod[periods[i]] = availableSpaceForEachPeriod
      }
      else {
        spacingForEachPeriod[periods[i]] = periodArticlesCountMap[periods[i]] * this.defaultImageSize
        const extraSpace = availableSpaceForEachPeriod - periodArticlesCountMap[periods[i]] * this.defaultImageSize
        if (modelGroupValue.size !== i + 1) {
          const spaceAddedToEach = Math.floor(extraSpace / (modelGroupValue.size - (i + 1)))
          availableSpaceForEachPeriod += spaceAddedToEach
        }
      }
    }
    return spacingForEachPeriod
  }

  findSpacingForAllPeriodsAsPerNewImageSize(modelGroupValue, imageSize) {
    let periodArticlesCountMap = {}
    const spacingForEachPeriod = {}
    for (const [period, articles] of modelGroupValue) {
      periodArticlesCountMap[period] = articles.length
    }
    periodArticlesCountMap = Object.entries(periodArticlesCountMap)
      .sort(([,a], [,b]) => a as number - (isNumber(b) ? b : 0))
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})

    const periods = Object.keys(periodArticlesCountMap)
    for (let i = 0; i < periods.length; i++) {
      spacingForEachPeriod[periods[i]] = periodArticlesCountMap[periods[i]] * imageSize
    }
    return spacingForEachPeriod
  }

  getDesignOptionScale(actualWidth, actualHeight, maxSize = 105) {
    let scaleX = 1
    let scaleY = 1

    if (actualWidth > actualHeight) {
      if (actualWidth > maxSize) {
        const newHeight = actualHeight * (maxSize / actualWidth)
        scaleX = maxSize / actualWidth
        scaleY = newHeight / actualHeight
      }
    }
    else {
      if (actualHeight > maxSize) {
        const newWidth = actualWidth * (maxSize / actualHeight)
        scaleX = newWidth / actualWidth
        scaleY = maxSize / actualHeight
      }
    }

    return Math.max(scaleX, scaleY)
  }

  async addModelHeader(article: MyArticle, includeDesignOptions: boolean, top: number, left: number, modelWidth: number, designOptionsData: any[]) {
    const objects: IWbObject[] = []
    const rowSpace = this.frameWidth - this.leftMarginForModel - 10 - this.rightMargin - this.frameVerticalHeaderWidth
    const text = article.ModelLifecycle ? article.ModelLifecycle.toString().toLowerCase() === 'carryover' ? 'C/O' : 'NEW' : ''
    objects.push(this.createTextBoxObject(text, left, top, 35, this.settings.modelDetails.fontSize, 'bold', 'left', text === 'C/O' ? '#000000' : '#FF5733'))
    const validData: any[] = []
    if (includeDesignOptions) {
      const nameLeft = left + 35
      const nameWidth = modelWidth > rowSpace ? rowSpace : modelWidth
      designOptionsData.forEach((data) => {
        const baseLeft = nameLeft + (nameWidth / 2)
        const linkX = data.left - baseLeft
        const linkY = data.top - top
        validData.push({
          id: data.id,
          scale: data.scale,
          link: { x: linkX, y: linkY },
        })
      })
    }
    const modelNameHeaderOpt = {
      left: left + 35,
      top,
      showLabels: this.settings.modelDetails.showLabels,
      width: modelWidth > rowSpace ? rowSpace : modelWidth,
      attributes: ['ModelName'],
      attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
      fontFamily: this.settings.modelDetails.fontFamily,
      fontSize: this.settings.modelDetails.fontSize,
      fontWeight: this.settings.modelDetails.fontWeight,
      textAlign: this.settings.modelDetails.textAlign,
      fill: this.settings.modelDetails.fill,
      showDesignOptions: includeDesignOptions,
      designOptionData: validData,
      allowDesignOption: true,
    }
    const modelNameHeaderObject = await WbModelDetails.loadModelDetails(article, modelNameHeaderOpt)
    objects.push(modelNameHeaderObject)
    const headerOpt = {
      left: left + 35,
      top: top + 15,
      showLabels: this.settings.modelDetails.showLabels,
      width: modelWidth > rowSpace ? rowSpace : modelWidth,
      attributes: this.settings.modelDetails.attributes,
      attributesSeparator: this.settings.modelDetails.attributesSeparator,
      attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
      fontFamily: this.settings.modelDetails.fontFamily,
      fontSize: this.settings.modelDetails.fontSize,
      fontWeight: this.settings.modelDetails.fontWeight,
      textAlign: this.settings.modelDetails.textAlign,
      fill: this.settings.modelDetails.fill,
      allowDesignOption: false,
    }
    const modelHeaderObject = await WbModelDetails.loadModelDetails(article, headerOpt)
    objects.push(modelHeaderObject)
    const skuCountOptions = {
      left,
      top: top + 60,
      showLabels: false,
      width: modelWidth > rowSpace ? rowSpace : modelWidth,
      attributes: ['currentSKuCount'],
      attributesSeparator: '',
      attributePlaceHolder: this.settings.modelDetails.attributePlaceHolder,
      fontFamily: this.settings.modelDetails.fontFamily,
      fontSize: this.settings.modelDetails.fontSize,
      fontWeight: this.settings.modelDetails.fontWeight,
      textAlign: this.settings.modelDetails.textAlign,
      fill: this.settings.modelDetails.fill,
      allowDesignOption: false,
    }
    const skuCountObject = await WbModelDetails.loadModelDetails(article, skuCountOptions)
    objects.push(skuCountObject)
    const skuText = skuCountObject.text?.split(': ')
    const textSkuCount = `Initial SKU Count: ${skuText && skuText.length === 2 ? skuText[1] : ''}`
    objects.push(this.createTextBoxObject(textSkuCount, left, top + 45, modelWidth > rowSpace ? rowSpace : modelWidth, this.settings.modelDetails.fontSize, this.settings.modelDetails.fontWeight, 'left', this.settings.modelDetails.fill))
    return objects
  }

  async addPeriodAndArticles(period: string, articles: MyArticle[] | DuneAsset[], options: Record<string, any>, periodSpacing: number, articleForDesignOption, top: number, left: number, isNeedToDistributeInMultipleRows, remainingSpaceInCurrentRow, isSecondRowDistributedModel, distributedModelData) {
    const objects: IWbObject[] = []
    const designOptionsObjects: any[] = []
    if (isSecondRowDistributedModel && distributedModelData && distributedModelData.articles) {
      const overlappingWidth = distributedModelData.overlappingWidth
      const articlesToConsider = distributedModelData.articles
      const periodLength = overlappingWidth === 0 ? this.imgSize * articlesToConsider.length : (overlappingWidth * (articlesToConsider.length - 1)) + this.imgSize

      objects.push(this.createLineObject(top, left, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left, periodLength + 5, 0, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left + periodLength + 5, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createTextBoxObject(period ? period === 'designOption' ? 'Design Option' : period : '', left, top + 5, periodLength, 12, 'normal', 'center', this.settings.articleDetails.fill))
      const imageTop = top + 20
      let imageLeft = left + 5

      for (let i = 0; i < articlesToConsider.length; i++) {
        const currentScaleFactor = (this.imgSize / this.settings.articleImage.width) * 100
        if (period === 'designOption') {
          designOptionsObjects.push({
            id: articlesToConsider[i].Id,
            scale: this.getDesignOptionScale(articlesToConsider[i].Width, articlesToConsider[i].Height),
            top: imageTop,
            left: imageLeft,
          })
          // const designObject = await this.createDesignOptionImage(imageTop, imageLeft, articlesToConsider[i] as DuneAsset, articleForDesignOption, currentScaleFactor)
          // objects.push(designObject as IWbObject)
        }
        else {
          const articleImageObj = await this.createImage(imageTop, imageLeft, articlesToConsider[i], currentScaleFactor, options.imageType)
          objects.push(articleImageObj)
          this.settings.articleDetails.attributes = articlesToConsider[i].ArticleLifecycle ? (articlesToConsider[i].ArticleLifecycle.toString().toLowerCase() === 'carryover' ? options.attributesToDisplayForCarryoverArticles : options.attributesToDisplayForNewArticles) : ['ArticleNumber']
          const headerOpt = {
            left: imageLeft,
            top: imageTop + this.imgSize + 5,
            showLabels: this.settings.articleDetails.showLabels,
            width: overlappingWidth === 0 ? this.imgSize : overlappingWidth - 35,
            attributes: this.settings.articleDetails.attributes,
            attributePlaceHolder: this.settings.articleDetails.attributePlaceHolder,
            fontFamily: this.settings.articleDetails.fontFamily,
            fontSize: this.settings.articleDetails.fontSize,
            fontWeight: this.settings.articleDetails.fontWeight,
            textAlign: this.settings.articleDetails.textAlign,
            fill: this.settings.articleDetails.fill,
          }
          const modelHeaderObject = await WbArticleDetails.loadArticleDetails(articlesToConsider[i], headerOpt)
          objects.push(modelHeaderObject)
        }
        imageLeft += overlappingWidth === 0 ? this.imgSize : overlappingWidth
      }
      return { objects, distributedModelData: {}, widthUsed: periodLength, designOptionsObjects }
    }
    else if (isNeedToDistributeInMultipleRows) {
      let overlappingWidth = 0
      let articlesToConsider = articles
      if (periodSpacing < (articles.length * this.imgSize)) {
        overlappingWidth = 70
        const withOverlapWidthCurrentArticlesSpacing = (overlappingWidth * (articles.length - 1)) + this.imgSize
        if (withOverlapWidthCurrentArticlesSpacing > periodSpacing) {
        // consider less articles as canot be accomodated in space
          const count = Math.floor(periodSpacing / 70)
          articlesToConsider = articles.slice(0, count)
        }
        else if (withOverlapWidthCurrentArticlesSpacing < periodSpacing) {
          overlappingWidth += 2
          while (((overlappingWidth * (articles.length - 1)) + this.imgSize) <= periodSpacing) {
            overlappingWidth += 2
          }
        }
      }
      let firstPart = articlesToConsider
      let secondPart: MyArticle[] | DuneAsset[] = []
      if (isNeedToDistributeInMultipleRows) {
        const numberOfArticlesAcommodateInFirstRow = Math.floor(remainingSpaceInCurrentRow / (overlappingWidth === 0 ? this.imgSize : overlappingWidth))
        firstPart = articlesToConsider.slice(0, numberOfArticlesAcommodateInFirstRow)
        secondPart = articlesToConsider.slice(numberOfArticlesAcommodateInFirstRow)
      }
      const periodLength = overlappingWidth === 0 ? this.imgSize * firstPart.length : (overlappingWidth * (firstPart.length - 1)) + this.imgSize
      objects.push(this.createLineObject(top, left, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left, periodLength + 5, 0, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left + periodLength + 5, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createTextBoxObject(period ? period === 'designOption' ? 'Design Option' : period : '', left, top + 5, periodLength, 12, 'normal', 'center', this.settings.articleDetails.fill))
      const imageTop = top + 20
      let imageLeft = left + 5
      for (let i = 0; i < firstPart.length; i++) {
        const currentScaleFactor = (this.imgSize / this.settings.articleImage.width) * 100
        if (period === 'designOption') {
          designOptionsObjects.push({
            id: articlesToConsider[i].Id,
            scale: this.getDesignOptionScale(articlesToConsider[i].Width, articlesToConsider[i].Height),
            top: imageTop,
            left: imageLeft,
          })
          //     const designObject = await this.createDesignOptionImage(imageTop, imageLeft, articlesToConsider[i] as DuneAsset, articleForDesignOption, currentScaleFactor)
          //  objects.push(designObject as IWbObject)
        }
        else {
          const articleData = articlesToConsider[i] as MyArticle
          const articleImageObj = await this.createImage(imageTop, imageLeft, articlesToConsider[i] as MyArticle, currentScaleFactor, options.imageType)
          objects.push(articleImageObj)
          this.settings.articleDetails.attributes = articleData.ArticleLifecycle ? (articleData.ArticleLifecycle.toString().toLowerCase() === 'carryover' ? options.attributesToDisplayForCarryoverArticles : options.attributesToDisplayForNewArticles) : ['ArticleNumber']
          const headerOpt = {
            left: imageLeft,
            top: imageTop + this.imgSize + 5,
            showLabels: this.settings.articleDetails.showLabels,
            width: overlappingWidth === 0 ? this.imgSize : overlappingWidth - 35,
            attributes: this.settings.articleDetails.attributes,
            attributePlaceHolder: this.settings.articleDetails.attributePlaceHolder,
            fontFamily: this.settings.articleDetails.fontFamily,
            fontSize: this.settings.articleDetails.fontSize,
            fontWeight: this.settings.articleDetails.fontWeight,
            textAlign: this.settings.articleDetails.textAlign,
            fill: this.settings.articleDetails.fill,
          }
          const modelHeaderObject = await WbArticleDetails.loadArticleDetails(articlesToConsider[i] as MyArticle, headerOpt)
          objects.push(modelHeaderObject)
        }
        imageLeft += overlappingWidth === 0 ? this.imgSize : overlappingWidth
      }
      return { objects, distributedModelData: { articles: secondPart, overlappingWidth, remainingPeriodSpacing: periodSpacing - remainingSpaceInCurrentRow, currentPeriod: period, designOptionsObjects } }
    }
    else {
      objects.push(this.createLineObject(top, left, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left, periodSpacing + 5, 0, this.settings.articleDetails.fill))
      objects.push(this.createLineObject(top, left + periodSpacing + 5, 20, 90, this.settings.articleDetails.fill))
      objects.push(this.createTextBoxObject(period ? period === 'designOption' ? 'Design Option' : period : '', left, top + 5, periodSpacing, 12, 'normal', 'center', this.settings.articleDetails.fill))
      const imageTop = top + 20
      let imageLeft = left + 5

      let overlappingWidth = 0
      let articlesToConsider = articles
      if (periodSpacing < (articles.length * this.imgSize)) {
        overlappingWidth = 70
        const withOverlapWidthCurrentArticlesSpacing = (overlappingWidth * (articles.length - 1)) + this.imgSize
        if (withOverlapWidthCurrentArticlesSpacing > periodSpacing) {
        // consider less articles as canot be accomodated in space
          const count = Math.floor(periodSpacing / 70)
          articlesToConsider = articles.slice(0, count)
        }
        else if (withOverlapWidthCurrentArticlesSpacing < periodSpacing) {
          overlappingWidth += 2
          while ((overlappingWidth * (articles.length - 1)) + this.imgSize <= periodSpacing) {
            overlappingWidth += 2
          }
        }
      }
      for (let i = 0; i < articlesToConsider.length; i++) {
        const currentScaleFactor = (this.imgSize / this.settings.articleImage.width) * 100
        if (period === 'designOption') {
          designOptionsObjects.push({
            id: articlesToConsider[i].Id,
            scale: this.getDesignOptionScale(articlesToConsider[i].Width, articlesToConsider[i].Height),
            top: imageTop,
            left: imageLeft,
          })
          // const designObject = await this.createDesignOptionImage(imageTop, imageLeft, articlesToConsider[i] as DuneAsset, articleForDesignOption, currentScaleFactor)
          // objects.push(designObject as IWbObject)
        }
        else {
          const articleData = articlesToConsider[i] as MyArticle
          const articleImageObj = await this.createImage(imageTop, imageLeft, articlesToConsider[i] as MyArticle, currentScaleFactor, options.imageType)
          objects.push(articleImageObj)
          this.settings.articleDetails.attributes = articleData.ArticleLifecycle ? (articleData.ArticleLifecycle.toString().toLowerCase() === 'carryover' ? options.attributesToDisplayForCarryoverArticles : options.attributesToDisplayForNewArticles) : ['ArticleNumber']
          const headerOpt = {
            left: imageLeft,
            top: imageTop + this.imgSize + 5,
            showLabels: this.settings.articleDetails.showLabels,
            width: overlappingWidth === 0 ? this.imgSize : overlappingWidth - 35,
            attributes: this.settings.articleDetails.attributes,
            attributePlaceHolder: this.settings.articleDetails.attributePlaceHolder,
            fontFamily: this.settings.articleDetails.fontFamily,
            fontSize: this.settings.articleDetails.fontSize,
            fontWeight: this.settings.articleDetails.fontWeight,
            textAlign: this.settings.articleDetails.textAlign,
            fill: this.settings.articleDetails.fill,
          }
          const modelHeaderObject = await WbArticleDetails.loadArticleDetails(articlesToConsider[i] as MyArticle, headerOpt)
          objects.push(modelHeaderObject)
        }

        imageLeft += overlappingWidth === 0 ? this.imgSize : overlappingWidth
      }
      return { objects, distributedModelData: {}, designOptionsObjects }
    }
  }

  changePropsAsPerFrameSize(frameSize: FrameSize) {
    this.frameWidth = frameSizes[frameSize].width
    this.frameHeight = frameSizes[frameSize].height
  }

  // objects functions
  createTextBoxObject(text: string, left: number, top: number, width: number, fontSize: number, fontWeight = 'normal', textAlign = 'center', fill = '#000000', backgroundColor = '', angle = 0) {
    return new WbTextBox(text, {
      top,
      left,
      width,
      fill,
      fontFamily: this.settings.textBox.fontFamily,
      fontSize,
      fontStyle: this.settings.textBox.fontStyle,
      fontWeight,
      textAlign,
      lock: false,
      backgroundColor,
      angle,
    })
  }

  createLineObject(top: number, left: number, width: number, angle = 0, stroke = '#000000') {
    const points: { x: number, y: number }[] = []
    if (angle === 0) {
      points.push({ x: left, y: top })
      points.push({ x: left + width, y: top })
    }
    else if (angle === 90) {
      points.push({ x: left, y: top })
      points.push({ x: left, y: top + width })
    }
    return new WbLine(points, {
      stroke,
    })
  }

  createRectangleObject(left: number, top: number, width: number, height: number, scaleX = 1, scaleY = 1, fill = 'transparent') {
    return new WbShape('rectangle', {
      top,
      left,
      width,
      height,
      fill,
      opacity: 1,
      scaleX,
      stroke: '#000000',
      scaleY,
      lock: true,
    })
  }

  async createHeaderNameLogo(top: number, left: number) {
    const options = {
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.uaNameLogoScaling / 100,
      scaleY: this.uaNameLogoScaling / 100,
    }
    return await WbImage.loadFromUrl(appConstants.logoPaths.uaNameLogo, options)
  }

  async createHeaderLogo(top: number, left: number) {
    const options = {
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      flipX: false,
      flipY: false,
      scaleX: this.logoScaling / 100,
      scaleY: this.logoScaling / 100,
    }
    return await WbImage.loadFromUrl(appConstants.logoPaths.uaIcon, options)
  }

  async createImage(top: number, left: number, article: MyArticle, imageScaleFactor: number, imageType: string) {
    const scaleFactor = imageScaleFactor / 100
    this.settings.articleImage.scale = imageScaleFactor
    return await WbArticleImage.loadArticleImage(article, 500, 500, { top, left, scaleX: scaleFactor, scaleY: scaleFactor, catalogCode: article.CatalogCode, articleId: article.Id, objectId: article.CatalogArticleId, isRequest: article._IsRequestArticle, assetKey: imageType })
  }

  async createDesignOptionImage(top: number, left: number, duneAsset: DuneAsset, article: MyArticle, imageScaleFactor: number) {
    const options = { article, localOnly: false, excludeFromGroupSelection: false, preventDelete: false, asset: duneAsset, assetId: duneAsset.Id, articleId: article.Id, assetStorageFile: duneAsset.StorageFile, top, left, scaleX: imageScaleFactor / 100, scaleY: imageScaleFactor / 100 }
    return await WbDesignOption.loadFromUrl(options, 500, 500)
  }
}

export default new ColorwayMerch()
